<template>
  <div v-loading="loading">
    <div class="header">
      <div class="filters">
        <el-select
          v-model="filterType"
          clearable
          placeholder="Tur"
          style="width: 200px"
          @change="onTypeChange"
          @clear="onTypeChange"
        >
          <el-option
            v-for="(label, val) in TYPE_LABELS"
            :key="val"
            :label="label"
            :value="val"
          />
        </el-select>

        <el-select
          v-if="subTypeOptions.length"
          v-model="filterSubType"
          clearable
          placeholder="Kichik tur"
          style="width: 170px"
          @change="resetPage"
          @clear="resetPage"
        >
          <el-option
            v-for="opt in subTypeOptions"
            :key="opt.value"
            :label="opt.label"
            :value="opt.value"
          />
        </el-select>

        <el-select
          v-model="filterPeriodType"
          clearable
          placeholder="Davr turi"
          style="width: 130px"
          @change="onPeriodChange"
          @clear="onPeriodChange"
        >
          <el-option value="YEARLY" label="Yillik" />
          <el-option value="QUARTERLY" label="Choraklik" />
        </el-select>

        <el-select
          v-if="filterPeriodType === 'YEARLY'"
          v-model="filterYear"
          clearable
          placeholder="Yil"
          style="width: 100px"
          @change="resetPage"
          @clear="resetPage"
        >
          <el-option v-for="y in yearOptions" :key="y" :label="y" :value="y" />
        </el-select>

        <el-select
          v-if="filterPeriodType === 'QUARTERLY'"
          v-model="filterQuarter"
          clearable
          placeholder="Chorak"
          style="width: 110px"
          @change="resetPage"
          @clear="resetPage"
        >
          <el-option v-for="q in [1, 2, 3, 4]" :key="q" :label="`${q}-chorak`" :value="q" />
        </el-select>

        <el-button @click="resetFilters">Tozalash</el-button>
      </div>

      <el-button type="primary" @click="$router.push({ name: 'DebtDataAdd' })">
        Qo'shish
      </el-button>
    </div>

    <el-table class="table-body" :data="tableData" border height="calc(100vh - 250px)">
      <el-table-column type="index" label="T/r" width="60" />
      <el-table-column label="Tur" min-width="180">
        <template #default="scope">
          {{ TYPE_LABELS[scope.row.type] || scope.row.type }}
        </template>
      </el-table-column>
      <el-table-column label="Kichik tur" width="160">
        <template #default="scope">
          {{ SUB_TYPE_LABELS[scope.row.sub_type] || '—' }}
        </template>
      </el-table-column>
      <el-table-column label="Davr turi" width="120">
        <template #default="scope">
          {{ scope.row.period_type === 'YEARLY' ? 'Yillik' : 'Choraklik' }}
        </template>
      </el-table-column>
      <el-table-column prop="year" label="Yil" width="80" align="center" />
      <el-table-column label="Chorak" width="90" align="center">
        <template #default="scope">
          <span v-if="scope.row.quarter">{{ scope.row.quarter }}-chorak</span>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column label="Excel fayl" min-width="150">
        <template #default="scope">
          {{ scope.row.excel_file_name || '—' }}
        </template>
      </el-table-column>
      <el-table-column label="SDMX fayl" min-width="150">
        <template #default="scope">
          {{ scope.row.sdmx_file_name || '—' }}
        </template>
      </el-table-column>
      <el-table-column label="Sana" width="130" align="center">
        <template #default="scope">
          {{ formatDate(scope.row.created_at) }}
        </template>
      </el-table-column>
      <el-table-column label="Amallar" width="175" align="center">
        <template #default="scope">
          <el-button
            size="small"
            type="primary"
            @click="$router.push({ name: 'DebtDataEdit', params: { id: scope.row.id } })"
          >
            Tahrirlash
          </el-button>
          <el-button size="small" type="danger" @click="deleteItem(scope.row.id)">
            O'chirish
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination">
      <div>Umumiy soni: {{ totalCount }}</div>
      <el-pagination
        background
        layout="sizes, prev, pager, next"
        :total="totalCount"
        v-model:page-size="limit"
        v-model:current-page="currentPage"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { ElMessage, ElMessageBox, ElNotification } from "element-plus";
import http from "@/http";
import { useRouter, useRoute } from "vue-router";

const router = useRouter();
const route = useRoute();

const loading = ref(false);
const tableData = ref([]);
const totalCount = ref(0);
const limit = ref(20);
const currentPage = ref(1);

const filterType = ref("");
const filterSubType = ref("");
const filterPeriodType = ref("");
const filterYear = ref("");
const filterQuarter = ref("");

const TYPE_LABELS = {
  GENERAL_OVERVIEW: "Umumiy sharh",
  DEBT_BY_CURRENCY: "Valyutalar kesimida",
  DEBT_BY_CREDITORS: "Kreditorlar kesimida",
  DEBT_BY_SECTORS: "Iqtisodiyot sektorlari kesimida",
};

const SUB_TYPE_LABELS = {
  NATIONAL: "Milliy valyuta",
  INTERNATIONAL: "Chet el valyutasi",
  EXTERNAL: "Tashqi kreditorlar",
  INTERNAL: "Ichki kreditorlar",
};

const subTypeOptions = computed(() => {
  if (filterType.value === "DEBT_BY_CURRENCY") {
    return [
      { value: "NATIONAL", label: "Milliy valyuta" },
      { value: "INTERNATIONAL", label: "Chet el valyutasi" },
    ];
  }
  if (filterType.value === "DEBT_BY_CREDITORS") {
    return [
      { value: "EXTERNAL", label: "Tashqi kreditorlar" },
      { value: "INTERNAL", label: "Ichki kreditorlar" },
    ];
  }
  return [];
});

const yearOptions = computed(() => {
  const current = new Date().getFullYear();
  return Array.from({ length: current - 2019 }, (_, i) => current - i);
});

const formatDate = (val) =>
  val ? new Date(val).toLocaleDateString("uz-UZ") : "—";

const onTypeChange = () => {
  filterSubType.value = "";
  resetPage();
};

const onPeriodChange = () => {
  filterYear.value = "";
  filterQuarter.value = "";
  resetPage();
};

const resetPage = () => {
  currentPage.value = 1;
  router.replace({
    query: {
      ...route.query,
      page: 1,
      type: filterType.value || undefined,
      sub_type: filterSubType.value || undefined,
      period_type: filterPeriodType.value || undefined,
      year: filterYear.value || undefined,
      quarter: filterQuarter.value || undefined,
    },
  });
  fetchList();
};

const resetFilters = () => {
  filterType.value = "";
  filterSubType.value = "";
  filterPeriodType.value = "";
  filterYear.value = "";
  filterQuarter.value = "";
  currentPage.value = 1;
  router.replace({ query: { size: limit.value } });
  fetchList();
};

const fetchList = () => {
  loading.value = true;
  const params = {
    limit: limit.value,
    offset: limit.value * (currentPage.value - 1),
  };
  if (filterType.value) params.type = filterType.value;
  if (filterSubType.value) params.sub_type = filterSubType.value;
  if (filterPeriodType.value) params.period_type = filterPeriodType.value;
  if (filterYear.value) params.year = filterYear.value;
  if (filterQuarter.value) params.quarter = filterQuarter.value;

  http
    .get("api/v1/debt-data", { params })
    .then(({ data }) => {
      tableData.value = data.result;
      totalCount.value = data.count;
    })
    .catch(({ response }) => {
      ElNotification.error({
        message: response?.data?.message || "Ma'lumot yuklashda xatolik sodir bo'ldi!",
      });
    })
    .finally(() => {
      loading.value = false;
    });
};

const deleteItem = (id) => {
  ElMessageBox.confirm("Davlat qarzi ma'lumotini o'chirasizmi?", "Diqqat!", {
    confirmButtonText: "Ha",
    cancelButtonText: "Bekor qilish",
    type: "warning",
  })
    .then(() => {
      loading.value = true;
      http
        .delete(`api/v1/debt-data/${id}`)
        .then(() => {
          ElMessage({ type: "success", message: "Muvaffaqqiyatli o'chirildi" });
          fetchList();
        })
        .catch(() => {
          ElNotification({ type: "error", title: "Xatolik", message: "Iltimos, qayta urinib ko'ring" });
        })
        .finally(() => {
          loading.value = false;
        });
    })
    .catch(() => {
      ElMessage({ type: "info", message: "O'chirish bekor qilindi" });
    });
};

const handleCurrentChange = () => {
  router.replace({ query: { ...route.query, page: currentPage.value } });
  fetchList();
};

const handleSizeChange = () => {
  currentPage.value = 1;
  router.replace({ query: { ...route.query, size: limit.value, page: 1 } });
  fetchList();
};

onMounted(() => {
  if (route.query.size) limit.value = parseInt(route.query.size);
  if (route.query.page) currentPage.value = parseInt(route.query.page);
  if (route.query.type) filterType.value = route.query.type;
  if (route.query.sub_type) filterSubType.value = route.query.sub_type;
  if (route.query.period_type) filterPeriodType.value = route.query.period_type;
  if (route.query.year) filterYear.value = route.query.year;
  if (route.query.quarter) filterQuarter.value = parseInt(route.query.quarter);
  fetchList();
});
</script>

<style scoped lang="scss">
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  overflow-x: auto;

  .filters {
    display: flex;
    flex-shrink: 0;
    gap: 8px;
    align-items: center;
  }
}
.pagination {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
}
</style>
